import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ErrorText } from 'components/ui/FormControls';
import { Snackbar } from 'components/ui/Feedback';
import React, { FC, useMemo, useState } from 'react';
import FroalaEditorComponent from 'react-froala-wysiwyg';
import type FroalaEditor from 'froala-editor';

import {
    useNotification,
    MessageTypes,
} from '../../../providers/NotificationProvider';
import { useLogger } from '../../../providers/logger/Logger';
import { LogLevel, LogAction } from '../../../providers/logger/logger-types';
import { config } from '../../../../config';

import sanitizeHtml from 'sanitize-html';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/css/themes/dark.min.css';
import 'froala-editor/css/plugins/line_breaker.min.css';
import '@fortawesome/fontawesome-free';
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/inline_style.min.js';
import 'froala-editor/js/plugins/file.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/draggable.min.js';
import 'froala-editor/js/plugins/video.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/quote.min.js';
import 'froala-editor/js/plugins/line_height.min.js';
import 'froala-editor/js/plugins/line_breaker.min.js';
import 'froala-editor/js/plugins/quick_insert.min.js';
import 'froala-editor/js/plugins/word_paste.min.js';
import 'froala-editor/js/plugins/print.min.js';
import 'froala-editor/js/plugins/table.min.js';
import { setBuzzsproutPlayerHeight } from '../../../../utils/utils';
import { TABLEAU_ENABLED, makeCharts } from '../../../../utils/tableau';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import ApolloClient from 'apollo-client';
import { useAuth0Data } from 'neon-auth0';
import type { Theme as ExtendedTheme } from '@mui/material/styles';

import { editorSx } from './styles';
import { applyDefaultCenterAlignmentToAllTables } from './table-alignment.utils';
import { useEditorPdfLightTheme } from './useEditorPdfLightTheme';
import { installFroalaFocusScrollGuard } from './focus-scroll.utils';

export interface EditorProps {
    initialHtml: string;
    onChange: (value: string) => void;
    errorMessage?: string;
    customOptions?: any;
    className?: string;
}

// Prevent Froala focus calls from scrolling the page.
installFroalaFocusScrollGuard();

let lastUploadedFilename = '';
let lastUploadedImageName = '';

const EDITOR_IMAGE_MAX_SIZE = 20 * 1024 * 1024;

const pendingPopupCleanupTimers = new WeakMap<
    any,
    Map<string, ReturnType<typeof setTimeout>[]>
>();

// Avoid closing the same upload popup more than once per upload.
const closedPopupsForCurrentUpload = new WeakMap<any, Set<string>>();

const resetUploadPopupCloseTracking = (
    editor: FroalaEditor,
    popupName: string
) => {
    closedPopupsForCurrentUpload.get(editor)?.delete(popupName);
};

const cancelPendingPopupCleanup = (editor: FroalaEditor, popupName: string) => {
    const popupTimers = pendingPopupCleanupTimers.get(editor);
    const scheduledTimers = popupTimers?.get(popupName);

    if (!scheduledTimers?.length) {
        return;
    }

    scheduledTimers.forEach((timerId) => clearTimeout(timerId));
    popupTimers?.delete(popupName);

    if (popupTimers?.size === 0) {
        pendingPopupCleanupTimers.delete(editor);
    }
};

const focusEditorWithoutScrolling = (editor: FroalaEditor) => {
    editor?.events?.focus?.();

    const editorElement = editor?.el?.matches?.('.fr-element')
        ? editor.el
        : editor?.el?.querySelector?.('.fr-element');

    (editorElement || editor?.el)?.focus?.({ preventScroll: true });
};

const restoreEditorInteractivity = (
    editor: FroalaEditor,
    shouldFocus = false
) => {
    editor?.edit?.on?.();
    editor?.toolbar?.enable?.();
    editor?.image?.exitEdit?.(true);
    editor?.selection?.clear?.();
    editor?.events?.enableBlur?.();

    if (shouldFocus) {
        focusEditorWithoutScrolling(editor);
    }
};

const getUploadErrorMessage = (response: any, fallbackMessage: string) => {
    if (!response) {
        return fallbackMessage;
    }

    if (typeof response === 'string') {
        try {
            const parsedResponse = JSON.parse(response);
            return (
                parsedResponse?.error ||
                parsedResponse?.message ||
                fallbackMessage
            );
        } catch {
            return response;
        }
    }

    return response?.error || response?.message || fallbackMessage;
};

const showUploadError = (
    editor: FroalaEditor,
    popupName: string,
    progressLayerSelector: string,
    errorMessage: string
) => {
    cancelPendingPopupCleanup(editor, popupName);

    const popup = editor?.popups?.get?.(popupName);
    if (popup?.length) {
        popup
            .find('.fr-layer.fr-pactive')
            .addClass('fr-active')
            .removeClass('fr-pactive');
        popup.find(progressLayerSelector).addClass('fr-error fr-active');
        popup.find('.fr-buttons').show();
    }

    restoreEditorInteractivity(editor);
    editor?.popups?.show?.(popupName);
    editor?.events?.trigger?.('focus', []);

    return errorMessage;
};

const closeUploadPopup = (
    editor: FroalaEditor,
    popupName: string,
    progressLayerSelector: string
) => {
    const closedPopups =
        closedPopupsForCurrentUpload.get(editor) ?? new Set<string>();
    if (closedPopups.has(popupName)) {
        return;
    }
    closedPopups.add(popupName);
    closedPopupsForCurrentUpload.set(editor, closedPopups);

    const hidePopup = () => {
        const popup = editor?.popups?.get?.(popupName);
        if (popup?.length) {
            popup
                .find('.fr-layer.fr-pactive')
                .addClass('fr-active')
                .removeClass('fr-pactive');
            popup.find(progressLayerSelector).removeClass('fr-active fr-error');
            popup.find('.fr-buttons').show();
        }

        restoreEditorInteractivity(editor, true);
        editor?.popups?.hide?.(popupName);
        editor?.popups?.hideAll?.();
    };

    cancelPendingPopupCleanup(editor, popupName);
    hidePopup();

    const popupTimers =
        pendingPopupCleanupTimers.get(editor) ??
        new Map<string, ReturnType<typeof setTimeout>[]>();
    const scheduledTimers = [
        setTimeout(() => {
            hidePopup();
            cancelPendingPopupCleanup(editor, popupName);
        }, 0),
    ];

    popupTimers.set(popupName, scheduledTimers);
    pendingPopupCleanupTimers.set(editor, popupTimers);
};

const deferPastePostProcessing = (callback: () => void) => {
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        window.requestAnimationFrame(callback);
        return;
    }

    setTimeout(callback, 0);
};

// Keep pasted layout and typography, but let the editor theme provide text and
// background colours. Documents copied from Word commonly contain explicit
// white fills and dark text which make pasted content unreadable in dark mode.
const pasteAllowedStyleProps = [
    'font-size',
    'width',
    'text-align',
    'vertical-align',
    'padding',
    'margin',
    'height',
    'margin-top',
    'margin-left',
    'margin-right',
    'margin-bottom',
    'text-decoration',
    'font-weight',
    'font-style',
    'text-indent',
    'border',
    'border-.*',
    'line-height',
    'list-style-type',
];

const editorConfig = (
    client: ApolloClient<object>,
    theme: ExtendedTheme,
    onError: (msg: string | JSX.Element, msgType?: MessageTypes) => void,
    onUploadErrorMessageChange: (msg?: string) => void,
    logger: any,
    exportEditorToPdf: (editor: FroalaEditor) => void,
    colorsText: string[],
    inlineStyles: Record<string, string>,
    customOptions: any = {}
) => {
    const {
        events,
        inlineStyles: customInlineStyles,
        colorsText: customColorsText,
        ...restCustomOptions
    } = customOptions;

    const mergedColorsText = [
        ...(Array.isArray(customColorsText) ? customColorsText : []),
        ...colorsText,
    ];

    const mergedInlineStyles = {
        ...(customInlineStyles || {}),
        ...inlineStyles,
    };

    return {
        key: 'dKA5cC2C2F2G2F1B5zPAENHMi1JPQRFZBTBa1WWEPYDbA2B6C4B3F4B2F3C3H2E2== ',
        theme: 'dark',
        attribution: false,
        heightMin: 600,
        placeholderText:
            'Start typing or insert rich content using the above toolbar.',
        pastePlain: false,
        toolbarSticky: true,
        toolbarStickyOffset: theme.spacing(6),
        paragraphFormat: {
            N: 'Normal text',
            H1: 'Heading 1',
            H2: 'Heading 2',
            H3: 'Heading 3',
        },
        // Preserve spreadsheet/table formatting on paste without importing
        // source document colours into the themed editor.
        pasteAllowedStyleProps,
        wordAllowedStyleProps: pasteAllowedStyleProps,
        pasteDeniedAttrs: ['script', 'color', 'bgcolor', 'background'],
        htmlRemoveTags: ['script', 'style'],
        fileUpload: true,
        fileAllowedTypes: ['application/pdf'],
        fileUploadURL: config.serverUrl + '/upload-file',
        fileMaxSize: 20 * 1024 * 1024,
        listAdvancedTypes: false,
        imageUploadURL: config.serverUrl + '/upload',
        imageMaxSize: EDITOR_IMAGE_MAX_SIZE,
        imageAllowedTypes: ['jpeg', 'jpg', 'png', 'gif'],
        imageSplitHTML: true,
        imageMove: true,
        imageDefaultAlign: 'center',
        imageDefaultWidth: 0,
        imageInsertButtons: ['imageBack', '|', 'imageUpload', 'imageByURL'],
        imageEditButtons: [
            'imageAlign',
            'imageDisplay',
            'imageCaption',
            'imageRemove',
        ],
        requestHeaders: {
            Authorization: '',
        },
        linkEditButtons: ['linkOpen', 'linkEdit', 'linkRemove'],
        linkInsertButtons: ['linkBack'],
        quickInsertButtons: ['image', 'video', 'ul', 'ol', 'hr'],
        toolbarButtons: {
            style: {
                buttons: ['paragraphFormat'],
            },
            moreText: {
                buttons: [
                    'bold',
                    'italic',
                    'underline',
                    'alignLeft',
                    'alignCenter',
                    'alignRight',
                    'inlineStyle',
                    'textColor',
                    'clearFormatting',
                    'outdent',
                    'indent',
                ],
                buttonsVisible: 14,
            },
            moreLists: {
                buttons: ['formatOL', 'formatUL'],
            },
            moreRich: {
                buttons: [
                    'insertImage',
                    'insertLink',
                    'insertVideo',
                    'insertTable',
                    'insertHR',
                    'promotionalContentDropdown',
                    'tableauCharts',
                    'getPDF',
                ],
                buttonsVisible: 8,
            },
            moreMisc: {
                buttons: ['undo', 'redo'],
                align: 'right',
            },
        },
        toolbarButtonsXS: {
            moreText: {
                buttons: [
                    'paragraphFormat',
                    'formatOL',
                    'formatUL',
                    'bold',
                    'italic',
                    'underline',
                    'alignLeft',
                    'alignCenter',
                    'alignRight',
                    'inlineStyle',
                    'textColor',
                    'clearFormatting',
                    'outdent',
                    'indent',
                ],
                buttonsVisible: 14,
            },
            moreRich: {
                buttons: [
                    'insertImage',
                    'insertLink',
                    'insertVideo',
                    'insertTable',
                    'insertHR',
                    'promotionalContentDropdown',
                    'getPDF',
                ],
                buttonsVisible: 2,
            },
            moreMisc: {
                buttons: ['undo', 'redo'],
                align: 'right',
            },
        },
        videoAllowedProviders: ['youtube', 'vimeo', 'spotify', 'buzzsprout'],
        videoInsertButtons: ['videoByURL', 'videoEmbed'],
        videoResponsive: true,
        tableEditButtons: [
            'tableHeader',
            'tableFooter',
            'tableRemove',
            'tableRows',
            'tableColumns',
            'tableStyle',
            '-',
            'tableCells',
            'tableCellBackground',
            'tableCellVerticalAlign',
            'tableCellHorizontalAlign',
        ],
        tableStyles: {
            'fr-table-align-left': 'Align Left',
            'fr-table-align-center': 'Align Center',
            'fr-table-align-right': 'Align Right',
        },
        tableMultipleStyles: false,
        tableDefaultAlign: 'center',
        tableResizer: true,
        wordPasteModal: false,
        wordPasteKeepFormatting: true,
        colorsStep: 7,
        colorsText: mergedColorsText,
        inlineStyles: mergedInlineStyles,
        events: {
            'paste.after': function () {
                deferPastePostProcessing(() => {
                    applyDefaultCenterAlignmentToAllTables(this?.el || null);
                    focusEditorWithoutScrolling(this);
                });
            },
            'image.beforeUpload': function (images: any[]) {
                cancelPendingPopupCleanup(this, 'image.insert');
                resetUploadPopupCloseTracking(this, 'image.insert');
                onUploadErrorMessageChange(undefined);
                lastUploadedImageName = images[0].name;
            },
            'image.error': function (error: any, response: any) {
                const uploadErrorMessage = showUploadError(
                    this,
                    'image.insert',
                    '.fr-image-progress-bar-layer',
                    getUploadErrorMessage(
                        response,
                        'Image upload failed. Please try again.'
                    )
                );
                onUploadErrorMessageChange(uploadErrorMessage);
                onError(uploadErrorMessage, MessageTypes.error);
                logger(LogLevel.WARN, LogAction.ImageUpload, 'Error', {
                    response,
                    error,
                    filename: lastUploadedImageName,
                });
            },
            'image.uploaded': function (response: any) {
                onUploadErrorMessageChange(undefined);
                logger(LogLevel.INFO, LogAction.ImageUpload, 'Uploaded', {
                    response,
                    filename: lastUploadedImageName,
                });
            },
            'image.inserted': function () {
                onUploadErrorMessageChange(undefined);
                closeUploadPopup(
                    this,
                    'image.insert',
                    '.fr-image-progress-bar-layer'
                );
            },
            'image.loaded': function () {
                onUploadErrorMessageChange(undefined);
                closeUploadPopup(
                    this,
                    'image.insert',
                    '.fr-image-progress-bar-layer'
                );
            },
            'file.error': function (error: any, response: any) {
                const uploadErrorMessage = showUploadError(
                    this,
                    'file.insert',
                    '.fr-file-progress-bar-layer',
                    getUploadErrorMessage(
                        response,
                        'File upload failed. Please try again.'
                    )
                );
                onUploadErrorMessageChange(uploadErrorMessage);
                onError(uploadErrorMessage, MessageTypes.error);
                logger(LogLevel.WARN, LogAction.FileUpload, 'Error', {
                    response,
                    error,
                });
            },
            'file.beforeUpload': function (files: any) {
                cancelPendingPopupCleanup(this, 'file.insert');
                resetUploadPopupCloseTracking(this, 'file.insert');
                onUploadErrorMessageChange(undefined);
                lastUploadedFilename = files[0].name;
            },
            'file.uploaded': function (response: any) {
                onUploadErrorMessageChange(undefined);
                const articleId = window.location.pathname
                    .split('/')
                    .slice(-1)?.[0];
                const links = JSON.parse(response).links;
                logger(LogLevel.INFO, LogAction.FileUpload, 'Uploaded PDF', {
                    filename: lastUploadedFilename,
                    articleId,
                    numPages: links.length,
                });

                const imagesHtml = links.reduce(
                    (html: string, imageUrl: string) =>
                        html +
                        `<p><img alt="${imageUrl}" src="${imageUrl}" class="fr-fic fr-dib"></p>`,
                    ''
                );

                const safeImagesHtml = sanitizeHtml(imagesHtml, {
                    allowedTags: ['p', 'img'],
                    allowedAttributes: { img: ['src', 'class'] },
                });

                (this as any).html.insert(safeImagesHtml);
                closeUploadPopup(
                    this,
                    'file.insert',
                    '.fr-file-progress-bar-layer'
                );
                return false;
            },
            initialized: function () {
                const webSpellChecker = (window as any).WEBSPELLCHECKER;

                if (this.print?.toPDF) {
                    this.print.toPDF = () => exportEditorToPdf(this);
                }

                webSpellChecker &&
                    webSpellChecker.init({
                        container: this.el,
                    });

                setBuzzsproutPlayerHeight();

                makeCharts(client, this.el);

                const observer = new MutationObserver(() =>
                    makeCharts(client, this.el)
                );
                observer.observe(this.el, { childList: true });
            },
            ...events,
        },
        ...restCustomOptions,
    };
};

const Editor: FC<EditorProps> = ({
    initialHtml,
    onChange,
    errorMessage,
    customOptions,
    className,
}) => {
    const theme = useTheme();
    const { printLog } = useLogger();
    const { showMessage, message, clearMessage, messageType } =
        useNotification();
    const [uploadErrorMessage, setUploadErrorMessage] = useState<
        string | undefined
    >(undefined);
    const client = useApolloClient();
    const { data } = useQuery(TABLEAU_ENABLED, { fetchPolicy: 'network-only' });
    const { accessToken } = useAuth0Data();
    const { colorsText, inlineStyles, exportEditorToPdf, normalizeEditorHtml } =
        useEditorPdfLightTheme(theme);

    const config = useMemo(() => {
        const conf = editorConfig(
            client,
            theme,
            showMessage,
            setUploadErrorMessage,
            printLog,
            exportEditorToPdf,
            colorsText,
            inlineStyles,
            customOptions
        );
        conf.requestHeaders.Authorization = `Bearer ${accessToken}`;
        const isTableau = !!data?.me.tableauAccess;
        if (!isTableau && conf.toolbarButtons.moreRich) {
            conf.toolbarButtons.moreRich.buttons =
                conf.toolbarButtons.moreRich.buttons.filter(
                    (button: string) => button !== 'tableauCharts'
                );
            conf.toolbarButtons.moreRich.buttonsVisible -= 1;
        }

        // Ensure highlight presets remain dark-background/white-text even when custom options are passed.
        conf.inlineStyles = {
            ...(conf.inlineStyles || {}),
            ...inlineStyles,
        };

        return conf;
    }, [
        accessToken,
        client,
        colorsText,
        customOptions,
        data?.me.tableauAccess,
        exportEditorToPdf,
        inlineStyles,
        printLog,
        showMessage,
        theme,
    ]);

    if (!data) return <></>;

    return (
        <Box className={className} sx={editorSx}>
            <FroalaEditorComponent
                config={config}
                model={initialHtml}
                onModelChange={(value: string) => {
                    onChange(normalizeEditorHtml(value));
                }}
            />
            <Snackbar
                open={!!message}
                message={message ?? ''}
                type={messageType ?? 'error'}
                onClose={clearMessage}
                onUndo={undefined}
            />
            <ErrorText sx={{ mt: 1 }}>{uploadErrorMessage}</ErrorText>
            <ErrorText>{errorMessage}</ErrorText>
        </Box>
    );
};

export default Editor;
